'use client';

import { Loader2, LogIn, KeyRound, User2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useActionState, useId } from 'react';
import { login, type LoginActionResult } from '@/app/actions/login';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/cn';

const INITIAL: LoginActionResult = { ok: true };

interface LoginFormProps {
  next: string;
}

export function LoginForm({ next }: LoginFormProps) {
  const t = useTranslations('auth');
  const [state, action, pending] = useActionState(login, INITIAL);
  const userId = useId();
  const passId = useId();

  return (
    <form action={action} className="flex flex-col gap-5">
      <input type="hidden" name="next" value={next} />

      <Field
        id={userId}
        name="username"
        label={t('login.fields.username')}
        type="text"
        autoComplete="username"
        icon={<User2 className="h-4 w-4" aria-hidden />}
        autoFocus
      />

      <Field
        id={passId}
        name="password"
        label={t('login.fields.password')}
        type="password"
        autoComplete="current-password"
        icon={<KeyRound className="h-4 w-4" aria-hidden />}
        trailing={
          <a
            href="#"
            className="text-[11px] font-medium uppercase tracking-wider text-foreground-muted transition hover:text-[var(--color-accent-quiet)]"
          >
            {t('login.forgotPassword')}
          </a>
        }
      />

      <label className="-mt-1 flex cursor-pointer items-center gap-2 text-[12px] text-foreground-muted select-none">
        <input
          type="checkbox"
          name="remember"
          className="peer sr-only"
          defaultChecked
        />
        <span
          aria-hidden
          className={cn(
            'grid h-4 w-4 place-items-center rounded-[4px] border border-border bg-surface-muted transition',
            'peer-checked:border-accent peer-checked:bg-accent',
            'peer-checked:[&_svg]:scale-100 peer-checked:[&_svg]:opacity-100',
            'peer-focus-visible:ring-2 peer-focus-visible:ring-ring',
          )}
        >
          <svg
            viewBox="0 0 16 16"
            className="h-3 w-3 scale-50 opacity-0 transition duration-150 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="3.25 8.5 6.75 12 12.75 4.75" />
          </svg>
        </span>
        {t('login.rememberDevice')}
      </label>

      {state.error ? (
        <div
          role="alert"
          className="flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2.5 text-xs text-danger"
        >
          <span aria-hidden className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-danger" />
          <span>{state.error}</span>
        </div>
      ) : null}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        disabled={pending}
        className="group relative mt-1 h-[50px] w-full rounded-[11px] shadow-[0_8px_24px_-12px_var(--color-accent)] transition hover:-translate-y-px hover:opacity-100 hover:brightness-105"
      >
        {pending ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        ) : (
          <LogIn className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden />
        )}
        {pending ? t('login.submitting') : t('login.submit')}
      </Button>

      <div className="relative my-1 flex items-center text-[10px] uppercase tracking-[0.32em] text-foreground-subtle">
        <span className="h-px flex-1 bg-border" />
        <span className="px-3">{t('login.orContinueWith')}</span>
        <span className="h-px flex-1 bg-border" />
      </div>

      <div className="grid grid-cols-2 gap-2.5">
        <OauthButton provider="Google" comingSoonLabel={t('login.oauthComingSoon')} />
        <OauthButton provider="Microsoft" comingSoonLabel={t('login.oauthComingSoon')} />
      </div>
    </form>
  );
}

interface FieldProps {
  id: string;
  name: string;
  label: string;
  type: string;
  autoComplete: string;
  icon: React.ReactNode;
  autoFocus?: boolean;
  trailing?: React.ReactNode;
}

function Field({ id, name, label, type, autoComplete, icon, autoFocus, trailing }: FieldProps) {
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-between">
        <label
          htmlFor={id}
          className="text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground-muted"
        >
          {label}
        </label>
        {trailing}
      </div>
      <div className="group relative">
        <span className="pointer-events-none absolute inset-y-0 left-0 grid w-11 place-items-center text-foreground-subtle transition group-focus-within:text-[var(--color-accent-quiet)]">
          {icon}
        </span>
        <input
          id={id}
          name={name}
          type={type}
          autoComplete={autoComplete}
          required
          autoFocus={autoFocus}
          className={cn(
            'h-12 w-full rounded-[11px] border border-border bg-surface-muted pl-11 pr-3.5 text-[14px] text-foreground',
            'placeholder:text-foreground-subtle',
            'outline-none transition focus:border-[var(--color-ring)] focus:shadow-[0_0_0_3px_var(--color-accent-glow)]',
          )}
        />
      </div>
    </div>
  );
}

function OauthButton({ provider, comingSoonLabel }: { provider: 'Google' | 'Microsoft'; comingSoonLabel: string }) {
  return (
    <button
      type="button"
      disabled
      aria-disabled
      title={comingSoonLabel}
      className={cn(
        'group flex h-10 items-center justify-center gap-2 rounded-lg border border-border bg-surface-muted/40 px-3 text-[12px] font-medium text-foreground/70',
        'cursor-not-allowed opacity-60 grayscale-[0.4]',
        'hover:cursor-not-allowed',
      )}
    >
      {provider === 'Google' ? <GoogleMark /> : <MicrosoftMark />}
      <span>{provider}</span>
    </button>
  );
}

function GoogleMark() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8a12 12 0 1 1 0-24c3 0 5.8 1.1 8 3l5.7-5.7A20 20 0 1 0 24 44c11 0 20-8 20-20 0-1.2-.1-2.3-.4-3.5z"/>
      <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 8 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"/>
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 12.7 28l-6.6 5.1A20 20 0 0 0 24 44z"/>
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.2-.1-2.3-.4-3.5z"/>
    </svg>
  );
}

function MicrosoftMark() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 23 23" aria-hidden>
      <rect x="1"  y="1"  width="10" height="10" fill="#F25022" />
      <rect x="12" y="1"  width="10" height="10" fill="#7FBA00" />
      <rect x="1"  y="12" width="10" height="10" fill="#00A4EF" />
      <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
    </svg>
  );
}
